import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Box, Chip, Typography } from "@mui/material";
import { useAuth } from "hooks/useAuth";
import {
  AI_DISCLOSURE_COPY,
  getAiDisclosureCopy,
} from "./aiDisclosureCopy";
import { fetchAiDisclosureCopyForLocale } from "./fetchAiDisclosureCopy";

/**
 * Patient-facing AI-not-a-person disclosure.
 *
 * @param {"facility" | "remote"} setting
 * @param {"banner" | "compact"} variant
 * @param {string} [locale] BCP-47
 */
export default function AiDisclosureNotice({
  setting = "remote",
  variant = "banner",
  locale = "en",
}) {
  const { user } = useAuth();
  const english = AI_DISCLOSURE_COPY.en;
  const [display, setDisplay] = useState(() => ({
    copy: getAiDisclosureCopy(locale),
    isTranslated: false,
    source: "en",
  }));

  useEffect(() => {
    let cancelled = false;
    setDisplay({
      copy: getAiDisclosureCopy("en"),
      isTranslated: false,
      source: "en",
    });
    fetchAiDisclosureCopyForLocale({ locale, uid: user?.uid || null }).then(
      (result) => {
        if (cancelled) return;
        setDisplay({
          copy: result.copy,
          isTranslated: result.isTranslated,
          source: result.source,
        });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [locale, user?.uid]);

  const copy = display.copy || english;
  const helpLine =
    setting === "facility" ? copy.facilityHelp : copy.remoteHelp;
  const englishHelp =
    setting === "facility" ? english.facilityHelp : english.remoteHelp;

  if (variant === "compact") {
    return (
      <Chip
        size="small"
        color="info"
        variant="outlined"
        label={copy.compactBadge || english.compactBadge}
        role="note"
        aria-label={copy.headline || english.headline}
        sx={{ fontWeight: 600 }}
      />
    );
  }

  return (
    <Alert
      severity="info"
      role="note"
      aria-label={copy.headline || english.headline}
      sx={{ mb: 2, textAlign: "left" }}
    >
      <Typography variant="subtitle2" component="p" sx={{ fontWeight: 700, mb: 0.5 }}>
        {copy.headline}
      </Typography>
      <Typography variant="body2" component="p" sx={{ mb: 0.5 }}>
        {copy.noHumanNow}
      </Typography>
      <Typography variant="body2" component="p" sx={{ mb: 0.5 }}>
        {helpLine}
      </Typography>
      <Typography variant="body2" component="p">
        {copy.emergency}
      </Typography>

      {display.isTranslated ? (
        <Box sx={{ mt: 1.5, pt: 1, borderTop: 1, borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ fontWeight: 600 }}>
            {english.headline}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="p">
            {english.noHumanNow}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="p">
            {englishHelp}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="p">
            {english.emergency}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            component="p"
            sx={{ mt: 0.5, fontStyle: "italic" }}
          >
            Machine-translated
          </Typography>
        </Box>
      ) : null}
    </Alert>
  );
}

AiDisclosureNotice.propTypes = {
  setting: PropTypes.oneOf(["facility", "remote"]),
  variant: PropTypes.oneOf(["banner", "compact"]),
  locale: PropTypes.string,
};
